import { type ComponentPropsWithoutRef, useState } from "react";
import { Check } from "lucide-react";
import type {
  Citation,
  DocumentFindingsResponse,
  PositionAnalysisResponse,
  PositionResponse,
} from "@patent-studio/api-types";
import { DisclosureToggle, useDisclosure } from "@/components/ui/Disclosure";
import { Panel } from "@/components/ui/Panel";
import { IconTile } from "@/components/ui/IconTile";
import { StatusPill } from "@/components/ui/StatusPill";
import { toneClasses } from "@/components/ui/tone";
import { cn } from "@/lib/utils";
import type { MappableElement } from "../positions/element-mapping";
import { AmendInPlace, AmendmentHistory } from "../positions/PositionRow";
import { AgainstPriorArt, reachOf } from "./AgainstPriorArt";
import { warnsBeforeConfirming } from "./ConfirmAgainstAdvice";
import {
  type AcknowledgeGaps,
  ComparedWith,
  MissingComparisons,
} from "./MissingComparisons";
import { NotAssessed } from "./NotAssessed";
import { VERDICT_LABELS, VERDICT_TONE, clearOf } from "@/lib/verdict-labels";
import { RestsOn } from "./StripMark";
import { VerdictBadge } from "@/components/shared/VerdictBadge";
import { FindingList } from "@/components/patterns/FindingList";
import {
  OverriddenPill,
  OverrideBox,
  OverrideFlash,
  OverrideReason,
  useOverride,
} from "./PositionRuling";
import { positionRowId } from "../anchors";
import { Actions, type AnalysisActions } from "./RowActions";

export type { AnalysisActions } from "./RowActions";

/**
 * The attorney's standing yes, drawn beside the verdict so both are visible.
 * A yes given against the tool's advice says so, off the verdict the server
 * recorded it against.
 */
function ConfirmedIndicator({ attorney }: { attorney: PositionResponse }) {
  const againstAdvice =
    attorney.confirmed_verdict !== null &&
    warnsBeforeConfirming(attorney.confirmed_verdict);
  return (
    <StatusPill
      tone={againstAdvice ? "warning" : "success"}
      icon={<Check className="h-3 w-3" aria-hidden />}
      data-testid="confirmed-indicator"
    >
      {againstAdvice ? "Confirmed against advice" : "Confirmed"}
    </StatusPill>
  );
}

/**
 * A verdict still waiting on the attorney's yes, marked as v0.28 marks it: a
 * small ring with "!" beside the verdict, named for a reader.
 */
function NotConfirmedMark() {
  return (
    <span role="img" aria-label="Not confirmed" title="Not confirmed">
      <IconTile tone="neutral" shape="round" size="xs">
        !
      </IconTile>
    </span>
  );
}

/**
 * The reasoning: what each document that reached the position discloses, and
 * what the attorney can do about it. A position nothing reaches says so.
 */
function Rationale({
  documents,
  referencesRead,
}: {
  documents: readonly DocumentFindingsResponse[];
  referencesRead: number;
}) {
  const reaching = documents.filter((d) => reachOf(d) !== null);
  if (reaching.length === 0) {
    return (
      <p className="text-detail text-muted-foreground">
        {clearOf(referencesRead)}.
      </p>
    );
  }
  return (
    <div className="grid gap-2">
      {reaching.map((document) => (
        <div key={document.file_id} className="text-detail leading-relaxed">
          <p>
            <b className="font-mono">
              {document.reference_id ?? document.source_file}
            </b>{" "}
            {document.rationale}
          </p>
          {document.remedy !== "" && (
            <p className="mt-0.5 text-muted-foreground">{document.remedy}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** The row's head: the number, the name, the verdict, the attorney's yes, and the expand. */
function Head({
  position,
  attorney,
  assessing,
  open,
  trigger,
}: {
  position: PositionAnalysisResponse;
  attorney: PositionResponse | undefined;
  assessing: boolean;
  open: boolean;
  trigger: ComponentPropsWithoutRef<"button">;
}) {
  // A re-run that moved the verdict says what from, so a yes given on the
  // old one can be reconsidered. A row not assessed has no verdict to have
  // moved, and a verdict of not assessed before is no verdict to have moved from.
  const moved =
    position.verdict !== "not_assessed" &&
    position.previous_verdict !== null &&
    position.previous_verdict !== "not_assessed" &&
    position.previous_verdict !== position.verdict
      ? position.previous_verdict
      : null;
  // The verdict's tint fades out across the head; the edge carries it down the row.
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2.5 px-[15px] py-[13px]",
        toneClasses(VERDICT_TONE[position.verdict], "wash"),
      )}
    >
      <span className="flex-none text-[11.5px] font-bold text-subtle-foreground">
        Position {position.number}
      </span>
      <span className="min-w-0 flex-1 truncate text-[13.5px] font-bold">
        {position.title}
      </span>
      <OverriddenPill ruled={attorney?.verdict_override ?? null} />
      <VerdictBadge verdict={position.verdict} />
      {assessing && (
        <span
          data-testid="assessing-marker"
          className="flex-none animate-pulse rounded-full border border-primary/20 bg-primary/[0.06] px-2 py-0.5 text-[10px] font-semibold text-primary whitespace-nowrap"
        >
          Assessing…
        </span>
      )}
      {moved !== null && (
        <StatusPill tone="warning" plain data-testid="verdict-moved">
          Verdict changed from {VERDICT_LABELS[moved]}
        </StatusPill>
      )}
      {attorney?.confirmed === true ? (
        <ConfirmedIndicator attorney={attorney} />
      ) : (
        position.verdict !== "not_assessed" && <NotConfirmedMark />
      )}
      <DisclosureToggle
        trigger={trigger}
        open={open}
        names={`position ${position.number}`}
      />
    </div>
  );
}

/** What a run that ruled the position found: why, what it was compared with, and the art that reaches it. */
function WhatTheRunFound({
  position,
  referencesRead,
  onOpenPassage,
}: {
  position: PositionAnalysisResponse;
  referencesRead: number;
  onOpenPassage: (fileId: string, citation: Citation) => void;
}) {
  return (
    <>
      <Rationale
        documents={position.documents}
        referencesRead={referencesRead}
      />
      <ComparedWith documents={position.assessed_against} />
      <AgainstPriorArt
        headline={position.headline}
        documents={position.documents}
        onOpenPassage={onOpenPassage}
      />
    </>
  );
}

/** What the run found, or why it found nothing, then what the stage found for the attorney to read. */
function Found({
  position,
  referencesRead,
  assessing,
  refusal,
  onOpenPassage,
}: {
  position: PositionAnalysisResponse;
  referencesRead: number;
  assessing: boolean;
  refusal: string | null;
  onOpenPassage: (fileId: string, citation: Citation) => void;
}) {
  return (
    <>
      {position.verdict === "not_assessed" ? (
        <NotAssessed
          position={position}
          assessing={assessing}
          refusal={refusal}
        />
      ) : (
        <WhatTheRunFound
          position={position}
          referencesRead={referencesRead}
          onOpenPassage={onOpenPassage}
        />
      )}
      <FindingList findings={position.findings} testId="position-finding" />
    </>
  );
}

/** What the attorney did to the position: how it was amended, and why they overrode its verdict. */
function AttorneyRecord({
  attorney,
}: {
  attorney: PositionResponse | undefined;
}) {
  if (attorney === undefined) return null;
  return (
    <>
      <AmendmentHistory position={attorney} />
      <OverrideReason reason={attorney.verdict_override_reason} />
    </>
  );
}

/** The detail: what the run found, or why it found nothing; what the position rests on; the actions. */
function Body({
  position,
  attorney,
  elements,
  actions,
  busy,
  assessing,
  refusal,
  referencesRead,
  onAssess,
  onAcknowledgeGaps,
  onOpenPassage,
  onEdit,
}: {
  position: PositionAnalysisResponse;
  referencesRead: number;
  attorney: PositionResponse | undefined;
  elements: readonly MappableElement[];
  actions: AnalysisActions;
  busy: boolean;
  assessing: boolean;
  refusal: string | null;
  onAssess: (positionId: string) => void;
  onAcknowledgeGaps: AcknowledgeGaps;
  onOpenPassage: (fileId: string, citation: Citation) => void;
  onEdit: () => void;
}) {
  const unruled = position.verdict === "not_assessed";
  const override = useOverride((verdict, reason) =>
    actions.onOverride(position.position_id, verdict, reason),
  );
  return (
    <div data-testid="analysis-body" className="mt-3 flex flex-col gap-3">
      <Found
        position={position}
        referencesRead={referencesRead}
        assessing={assessing}
        refusal={refusal}
        onOpenPassage={onOpenPassage}
      />
      <OverrideFlash set={override.flash} />
      <RestsOn
        attorney={attorney}
        elements={elements}
        strip={position.strip}
        busy={busy}
        onMark={actions.onMark}
      />
      <AttorneyRecord attorney={attorney} />
      {attorney !== undefined && override.open && !unruled && (
        <OverrideBox
          analysis={position.verdict}
          ruled={attorney.verdict_override}
          busy={busy}
          onOverride={override.save}
          onClose={override.close}
        />
      )}
      {attorney !== undefined && (
        <Actions
          position={position}
          attorney={attorney}
          actions={actions}
          busy={busy}
          assessing={assessing}
          onAssess={onAssess}
          onAcknowledgeGaps={onAcknowledgeGaps}
          onEdit={onEdit}
          onOverride={unruled ? null : override.start}
        />
      )}
    </div>
  );
}

/**
 * One position as the run ruled it: its number, its name, its verdict and
 * the attorney's yes in the head; the reasoning, the prior art it was tested
 * against, what it rests on, the notes and the actions in the body. The
 * verdict comes from the analysis and the attorney's state from the positions,
 * joined by id, so neither is derived from the other.
 */
interface AnalysisRowProps {
  position: PositionAnalysisResponse;
  attorney: PositionResponse | undefined;
  elements: readonly MappableElement[];
  actions: AnalysisActions;
  busy: boolean;
  onOpenPassage: (fileId: string, citation: Citation) => void;
  /** Assess this position alone, for one the run has not ruled. */
  onAssess: (positionId: string) => void;
  /** Confirm the position without the documents it was not compared with. */
  onAcknowledgeGaps: AcknowledgeGaps;
  /** Why the server would not confirm it without them, or nothing. */
  acknowledgeRefusal?: string | null;
  /** A run narrowed to this position is in flight. */
  assessing?: boolean;
  /** Why the server refused to assess this position alone, or nothing. */
  refusal?: string | null;
  /** How many documents the run read, which every "clear" names. */
  referencesRead: number;
}

export function AnalysisRow({
  position,
  attorney,
  elements,
  actions,
  busy,
  onOpenPassage,
  onAssess,
  onAcknowledgeGaps,
  acknowledgeRefusal = null,
  assessing = false,
  refusal = null,
  referencesRead,
}: AnalysisRowProps) {
  const { open, setOpen, trigger, panel } = useDisclosure();
  const [editing, setEditing] = useState(false);
  return (
    <Panel
      as="li"
      shape="row"
      id={positionRowId(position.position_id)}
      // Focusable by script alone: the footer lands here when it points at
      // this position, and a list item takes focus only when told it may.
      tabIndex={-1}
      data-testid={`analysis-row-${position.position_id}`}
      data-verdict={position.verdict}
      pad="none"
      edge={VERDICT_TONE[position.verdict]}
      className="mb-2.5 overflow-hidden"
    >
      <Head
        position={position}
        attorney={attorney}
        assessing={assessing}
        open={open}
        trigger={{
          ...trigger,
          onClick: () => {
            // A collapsed row reopens on its read view.
            setEditing(false);
            setOpen((wasOpen) => !wasOpen);
          },
        }}
      />
      <MissingComparisons
        position={position}
        attorney={attorney}
        busy={busy}
        assessing={assessing}
        onAssess={onAssess}
        onAcknowledgeGaps={onAcknowledgeGaps}
        refusal={acknowledgeRefusal}
      />
      <div {...panel} className="px-[15px] pb-[13px]">
        {open && editing && attorney !== undefined && (
          <AmendInPlace
            position={attorney}
            elements={elements}
            onAmend={actions.onAmend}
            busy={busy}
            onDone={() => setEditing(false)}
          />
        )}
        {open && !editing && (
          <Body
            position={position}
            attorney={attorney}
            elements={elements}
            actions={actions}
            busy={busy}
            assessing={assessing}
            refusal={refusal}
            referencesRead={referencesRead}
            onAssess={onAssess}
            onAcknowledgeGaps={onAcknowledgeGaps}
            onOpenPassage={onOpenPassage}
            onEdit={() => setEditing(true)}
          />
        )}
      </div>
    </Panel>
  );
}
